import { Component, computed, DestroyRef, inject, OnInit, signal, Signal } from '@angular/core';
import { NbInputModule, NbListModule, NbOptionModule, NbSelectModule } from '@nebular/theme';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { IInvestmentWizard } from '../wizard-interface';
import { KPI } from '../../../../../@core/api';
import { InvestmentWizardService } from '../../../../../@core/utils/investment-wizard-service';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ScopeService } from '../../../../../@core/utils';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { idMapToArray } from '../../../../../@core/utils/scope.service';
import { filter } from 'rxjs';
import { AssetTypeWithHierarchy } from '../../../asset-type-selector/asset-type-selector.component';
import { FormsModule } from '@angular/forms';


export type NewAssetWizardState<T = any> = {
  investmentType: KPI.InvestmentType
  value: T
}

@Component({
  selector: 'app-new-asset-wizard-step',
  standalone: true,
  imports: [
    NbListModule,
    CommonModule,
    NbInputModule,
    NbOptionModule,
    NbSelectModule,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
  ],
  template: `
    <div class="wrapper">
      <div class="card card-text border-0 rounded-0">
        <div class="list-group">
          @if (assetState) {
            <li class="list-group-item rounded-0">
              <p class="text-danger m-0">{{ 'Current equipment' | translate }}</p>
              <p class="my-1">{{ assetState?.label }}</p>
            </li>
          }
          <div class="card-header card-header py-4 rounded-0 text-light fs-6" style="background-color: #4f9137">
            {{ 'Future equipment' | translate }}
          </div>
          <form [formGroup]="newAssetForm" class="investment-form" autocomplete="off">
            <div class="form-group">
              <!-- Search bar -->
              <div class="search-container">
                <div class="search-input-wrapper">
                  <i class="fa-light fa-magnifying-glass search-icon"></i>
                  <input
                    type="text"
                    class="search-input"
                    [placeholder]="'Indicate a keyword' | translate"
                    [(ngModel)]="searchQueryValue"
                    [ngModelOptions]="{standalone: true}"
                    (ngModelChange)="onSearchChange($event)"
                  />
                  @if (searchQueryValue) {
                    <button type="button" class="clear-btn" (click)="clearSearch()" tabindex="-1">
                      <i class="fa-solid fa-times"></i>
                    </button>
                  }
                </div>
              </div>

              <!-- Search results -->
              @if (searchQueryValue.trim()) {
                @if (searchResults().length > 0) {
                  @for (result of searchResults(); track result.id) {
                    <div class="form-field search-result-item" [class.attention]="hasAttentionToFill()" (click)="selectSearchResult(result)">
                      <span class="result-name">{{ result.name }}</span>
                      <span class="result-path">{{ result.hierarchyPath }}</span>
                    </div>
                  }
                } @else {
                  <div class="form-field">
                    <span class="text-muted">{{ 'No results, please modify your search' | translate }}</span>
                  </div>
                }
              }

              <!-- Cascade dropdowns (hidden when searching) -->
              @if (!searchQueryValue.trim()) {
                <!-- SUPER CATEGORY -->
                <div class="form-field" [class.attention]="hasAttentionToFill()">
                  <label for="super_category">{{ 'Super category' | translate }}</label>
                  <nb-select id="super_category" fullWidth formControlName="super_category"
                             (selectedChange)="nomenclatureChanged('super_category', $event)" placeholder="-">
                    @for (superCategory of filteredSuperCategories(); track superCategory.id) {
                      <nb-option [value]="superCategory">
                        {{ superCategory.name }}
                      </nb-option>
                    }
                  </nb-select>
                </div>

                <!-- CATEGORY -->
                <div class="form-field" [class.disabled]="!filteredCategories().length"
                     [class.attention]="hasAttentionToFill()">
                  <label for="category">{{ 'Category' | translate }}</label>
                  <nb-select id="category" fullWidth placeholder="-" formControlName="category"
                             (selectedChange)="nomenclatureChanged('category', $event)">
                    @for (category of filteredCategories(); track category.id) {
                      <nb-option [value]="category">
                        {{ category.name }}
                      </nb-option>
                    }
                  </nb-select>
                </div>

                <!-- SUB CATEGORY -->
                <div class="form-field" [class.disabled]="!filteredSubCategories()?.length"
                     [class.attention]="hasAttentionToFill()">
                  <label for="sub_category">{{ 'Sub category' | translate }}</label>
                  <nb-select id="sub_category" fullWidth placeholder="-"
                             (selectedChange)="nomenclatureChanged('sub_category', $event)"
                             formControlName="sub_category">
                    @for (subCategory of filteredSubCategories(); track subCategory.id) {
                      <nb-option [value]="subCategory">
                        {{ subCategory.name }}
                      </nb-option>
                    }
                  </nb-select>
                </div>

                <!-- ASSET TYPE -->
                <div class="form-field" [class.disabled]="!filteredAssetTypes()?.length"
                     [class.attention]="hasAttentionToFill()">
                  <label for="asset_type">{{ 'Asset type' | translate }}</label>
                  <nb-select id="asset_type" fullWidth formControlName="asset_type" placeholder="-">
                    @for (assetType of filteredAssetTypes(); track assetType.id) {
                      <nb-option [value]="assetType">
                        {{ assetType.name }}
                      </nb-option>
                    }
                  </nb-select>
                </div>
              }

              <!-- Quantity -->
              <div class="form-field">
                <label for="quantity">{{ "Quantity" | translate }}</label>
                <input nbInput id="quantity" type="text" formControlName="quantity" fullWidth placeholder="-" />
              </div>

              <!-- Label -->
              <div class="form-field">
                <label for="label">
                  {{ "Future equipment label" | translate }}
                </label>
                <input nbInput id="label" formControlName="label" fullWidth [placeholder]="labelPlaceholder || '-'" (input)="onLabelInput()"/>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .search-container {
      padding: 7px 10px;
      flex-shrink: 0;
      background-color: #fff;
    }
    .search-input-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
    }
    .search-icon {
      flex: 0 0 auto;
      color: #8f9bb3;
      font-size: 14px;
      pointer-events: none;
    }
    .search-input {
      flex: 1 1 auto;
      min-width: 0;
      border: none;
      border-radius: 0;
      font-size: 14px;
      background-color: #fff;
      color: #222b45;
      line-height: 1.5;
      box-sizing: border-box;
      &:focus { outline: none; }
      &::placeholder { color: #8f9bb3; }
    }
    .clear-btn {
      flex: 0 0 auto;
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      color: #8f9bb3;
      font-size: 14px;
      transition: color 0.2s ease;
      z-index: 2;
      &:hover { color: #222b45; }
      i { display: block; }
    }
    .search-result-item {
      cursor: pointer;
      &:hover { background-color: #f0f8ff !important; }
    }
    .result-name {
      font-size: 13px;
      color: #222b45;
      display: block;
    }
    .result-path {
      font-size: 12px;
      color: #a78219;
      display: block;
    }
  `],
  styleUrls: ['../wizard-index.component.scss', '../../../side-forms/base-form/base-form.component.scss'],
})
export class WizardNewAssetStepComponent implements OnInit, IInvestmentWizard {
  readonly stateKey = 'NEW_ASSET_STEP';
  state = () => this.wizardService.getState(this.stateKey);

  investmentTypeState!: KPI.InvestmentType;
  assetState?: KPI.Asset;
  searchQueryValue = '';
  private labelManuallyEdited = false;
  labelPlaceholder: string = null;

  // dependencies
  private wizardService = inject(InvestmentWizardService);
  private scopeService = inject(ScopeService);
  private destroyRef = inject(DestroyRef);

  // main form
  newAssetForm = new FormGroup({
    label: new FormControl(null, Validators.required),
    super_category: new FormControl(null, Validators.required),
    category: new FormControl(null, Validators.required),
    sub_category: new FormControl(null, Validators.required),
    asset_type: new FormControl(null, Validators.required),
    quantity: new FormControl<number>(null, Validators.required),
    perimeter: new FormControl<KPI.Perimeter>(null, Validators.required),
  });

  // nomenclature signals
  private assetTypes = toSignal(this.scopeService.assetTypes$.pipe(idMapToArray()), { initialValue: [] as KPI.AssetType[] });
  private mono_ps = toSignal(this.scopeService.monoperimeters$.pipe(idMapToArray()), { initialValue: [] as KPI.Perimeter[] });

  filteredSuperCategories: Signal<KPI.SuperCategory[]> = toSignal(this.scopeService.superCategories$.pipe(idMapToArray()), { initialValue: [] as KPI.SuperCategory[] });
  private selectedSuperCategory = signal<KPI.SuperCategory>(null);

  private categories = toSignal(this.scopeService.categories$.pipe(idMapToArray()), { initialValue: [] as KPI.Category[] });
  private selectedCategory = signal<KPI.Category>(null);
  filteredCategories = computed(() =>
    this.categories()?.filter((category) => category.parent === this.selectedSuperCategory()?.id) ?? []
  );

  private subCategories = toSignal(this.scopeService.subCategories$.pipe(idMapToArray()), { initialValue: [] as KPI.SubCategory[] });
  private selectedSubCategory = signal<KPI.SubCategory>(null);
  filteredSubCategories: Signal<KPI.SubCategory[]> = computed(() =>
    this.subCategories()?.filter((subCategory) => subCategory.parent === this.selectedCategory()?.id)
  );

  filteredAssetTypes: Signal<KPI.AssetType[]> = computed(() =>
    this.assetTypes()?.filter((assetType) => assetType.parent === this.selectedSubCategory()?.id)
  );

  // search
  private searchQuery = signal<string>('');
  searchResults = computed((): AssetTypeWithHierarchy[] => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) return [];
    return this.assetTypes()
      .filter(at => {
        // search only on asset type name and its keywords
        if (at.name.toLowerCase().includes(query)) return true;
        const keywords: string[] = ((at as any).keywords || []).map((k: any) => (k.name || '').toLowerCase());
        return keywords.some(k => k.includes(query));
      })
      .slice(0, 20)
      .map(at => {
        const subCat = this.subCategories().find(sc => sc.id === at.parent);
        const cat = subCat ? this.categories().find(c => c.id === subCat.parent) : null;
        const superCat = cat ? this.filteredSuperCategories().find(sc => sc.id === cat.parent) : null;
        return {
          ...at,
          super_category: superCat,
          category: cat,
          sub_category: subCat,
          hierarchyPath: [superCat?.name, cat?.name, subCat?.name].filter(Boolean).join(' > '),
        } as AssetTypeWithHierarchy;
      });
  });

  ngOnInit() {
    const mono_ps = this.mono_ps();
    this.investmentTypeState = this.wizardService.getState('INVESTMENT_TYPE_SELECTION');
    this.assetState = this.wizardService.getState('ASSET_SELECTION');

    this.newAssetForm.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(value => {
      this.wizardService.setState('NEW_ASSET_STEP', {
        investmentType: this.investmentTypeState,
        value: { ...value, label: value.label?.trim() || value.label },
      });
      this.formValid();
    });

    // restore previous state
    if (this.state()) {
      const state = this.state().value;
      const { sub_category, category, super_category } = state;
      this.selectedSuperCategory.set(super_category);
      this.selectedCategory.set(category);
      this.selectedSubCategory.set(sub_category);
      this.newAssetForm.patchValue(state);

    // replacement / creation_of_equipment with existing asset
    } else if ((this.investmentTypeState.replacement || this.investmentTypeState.creation_of_equipment) && this.assetState) {
      const perimeter = mono_ps.find(m => m.building_id === this.assetState?.building);
      const assetType = this.assetTypes().find(at => at.id === this.assetState?.asset_type);
      const subCategory = this.subCategories().find(sc => sc.id === assetType?.parent);
      const category = this.categories().find(c => c.id === subCategory?.parent);
      const superCategory = this.filteredSuperCategories().find(sc => sc.id === category?.parent);

      this.selectedSuperCategory.set(superCategory);
      this.selectedCategory.set(category);
      this.selectedSubCategory.set(subCategory);

      const isSameTypeAsCurrentAsset = this.investmentTypeState.replacement
        && assetType
        && assetType.id === this.assetState.asset_type;
      this.newAssetForm.patchValue({
        label: isSameTypeAsCurrentAsset ? (this.assetState.label ?? assetType.name) : (assetType?.name ?? null),
        super_category: superCategory,
        category: category,
        sub_category: subCategory,
        asset_type: assetType,
        perimeter: perimeter,
        quantity: this.assetState.quantity,
      });

    // new asset without replacement
    } else if (!this.assetState) {
      const monoP = this.wizardService.getState('MONO_PERIMETER_SELECTION');
      const originAsset = this.wizardService.getState('ORIGIN_ASSET');
      this.newAssetForm.patchValue({
        label: '',
        super_category: null,
        category: null,
        sub_category: null,
        asset_type: null,
        perimeter: monoP,
        quantity: originAsset?.quantity ?? 1,
      });
    }

    this.newAssetForm.get('asset_type').valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
      filter((assetType) => !!assetType)
    ).subscribe((rawAssetType) => {
      if (!rawAssetType || this.labelManuallyEdited) return;
      const assetType = rawAssetType as KPI.AssetType;
      const isSameTypeAsCurrentAsset = !!(
        this.investmentTypeState?.replacement
        && this.assetState
        && assetType.id === this.assetState.asset_type
      );
      const newLabel = isSameTypeAsCurrentAsset ? (this.assetState!.label ?? assetType.name) : assetType.name;
      this.newAssetForm.patchValue({ label: newLabel as any });
    });

    this.formValid();
  }

  onSearchChange(query: string) {
    this.searchQuery.set(query);
  }

  clearSearch() {
    this.searchQueryValue = '';
    this.searchQuery.set('');
  }

  selectSearchResult(result: AssetTypeWithHierarchy) {
    // use original KPI.AssetType object so nb-select option reference matches
    const assetType = this.assetTypes().find(at => at.id === result.id) ?? result;
    this.selectedSuperCategory.set(result.super_category);
    this.selectedCategory.set(result.category);
    this.selectedSubCategory.set(result.sub_category);
    this.newAssetForm.patchValue({
      super_category: result.super_category,
      category: result.category,
      sub_category: result.sub_category,
      asset_type: assetType,
    });
    if (this.assetState) {
      this.labelManuallyEdited = true;
    }
    this.clearSearch();
  }

  onLabelInput() {
    this.labelManuallyEdited = true;
  }

  hasAttentionToFill() {
    return this.investmentTypeState.creation_of_equipment || this.investmentTypeState.replacement;
  }

  formValid() {
    const hasLabel = !!(this.newAssetForm.get('label').value?.trim() || this.labelPlaceholder);
    const otherFieldsValid = ['super_category', 'category', 'sub_category', 'asset_type', 'quantity', 'perimeter']
      .every(f => this.newAssetForm.get(f).valid);
    if ((this.investmentTypeState.creation_of_equipment || this.investmentTypeState.replacement) && !(hasLabel && otherFieldsValid)) {
      this.wizardService.blockCurrentStep('Creation of equipment requires replacement asset details');
    } else {
      this.wizardService.unblockCurrentStep();
    }
  }

  nomenclatureChanged(field: 'super_category' | 'category' | 'sub_category', $event: KPI.SuperCategory | KPI.Category | KPI.SubCategory) {
    if (field === 'super_category') {
      this.selectedSuperCategory.set(<KPI.SuperCategory>$event);
      this.selectedCategory.set(null);
      this.selectedSubCategory.set(null);
      this.newAssetForm.patchValue({ asset_type: null, sub_category: null, category: null });
    } else if (field === 'category') {
      this.selectedCategory.set(<KPI.Category>$event);
      this.selectedSubCategory.set(null);
      this.newAssetForm.patchValue({ asset_type: null, sub_category: null });
    } else if (field === 'sub_category') {
      this.selectedSubCategory.set(<KPI.SubCategory>$event);
      this.newAssetForm.patchValue({ asset_type: null });
    }
  }
}
